import { getRelationshipMeetings } from 'components/mentorship/CohortRelationshipSpotlight'
import type { EventsListItem } from 'components/mentorship/EventsListCard'
import { Button } from 'components/ui/button'
import { useTimezone } from 'contexts/TimezoneContext'
import type { MentorshipRelationship } from 'services/api/mentorshipContinuityClient'
import { resolveMeetingTimeZone } from 'utils/meetingTime'

type Props = {
  relationship: MentorshipRelationship
  events: EventsListItem[]
  onJoinMeeting?: (event: EventsListItem) => void
  onSchedule?: (relationship: MentorshipRelationship) => void
  onViewProfile?: () => void
  scheduleRequestEmail?: string | null
}

/** Uses the pair's actual invitation audience, including administrator-created 1:1s. */
export function RelationshipMeetingActions({ relationship, events, onJoinMeeting, onSchedule, onViewProfile, scheduleRequestEmail }: Props) {
  const { timezone } = useTimezone()
  const { next } = getRelationshipMeetings(events, relationship.cohort_id, String(relationship.mentor.id), relationship.mentee.id)
  if (!relationship.is_current) return onViewProfile ? <Button variant="outline" onClick={onViewProfile}>View profile</Button> : null
  const start = next?.startTimeIso ? new Date(next.startTimeIso) : null
  const end = next?.endTimeIso ? new Date(next.endTimeIso) : null
  const validStart = start && !Number.isNaN(start.getTime()) ? start : null
  const dateLabel = validStart?.toLocaleString(undefined, {
    timeZone: resolveMeetingTimeZone(next?.timeZone, timezone), weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  })
  const calendarDate = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')
  const calendarParams = next && validStart && end && !Number.isNaN(end.getTime()) ? new URLSearchParams({
    action: 'TEMPLATE', text: next.title, dates: `${calendarDate(validStart)}/${calendarDate(end)}`,
    details: next.description || '', location: next.isVirtual ? 'Online' : next.location || '',
  }) : null

  return <div className="space-y-3 rounded-xl border border-[var(--cohort-rule)] bg-[var(--cohort-paper)] p-4">
    <div>
      <p className="text-xs uppercase tracking-[0.06em] text-[var(--cohort-ink-muted)]">Next 1:1</p>
      <p className="mt-1 text-sm font-medium">{next ? dateLabel || next.date : 'No 1:1 scheduled yet'}</p>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {next?.isVirtual && next.canJoinVideo && onJoinMeeting ? <Button size="auto" onClick={() => onJoinMeeting(next)} className="cohort-primary-button px-4">Join 1:1</Button> : null}
      {calendarParams ? <a className="cohort-secondary-button inline-flex min-h-11 items-center rounded-xl px-3 text-sm" target="_blank" rel="noreferrer" href={`https://calendar.google.com/calendar/render?${calendarParams.toString()}`}>Add to calendar</a> : null}
      {onSchedule ? <Button size="auto" variant="outline" className="cohort-secondary-button px-3" onClick={() => onSchedule(relationship)}>Schedule 1:1</Button> : scheduleRequestEmail ? <a className="cohort-secondary-button inline-flex min-h-11 items-center rounded-xl px-3 text-sm" href={`mailto:${scheduleRequestEmail}?subject=Schedule%20a%201%3A1`}>Ask to schedule</a> : null}
      {onViewProfile ? <button type="button" className="cohort-text-action min-h-11 px-2 text-sm" onClick={onViewProfile}>View profile</button> : null}
    </div>
  </div>
}
